/** Product name, used as the document-title suffix and skip-link/announcement copy. */
export const APP_NAME = 'LangHub';

/**
 * id of the routed main-content wrapper in AppShell. The skip link targets it by
 * fragment (`#main-content`) and it carries `tabIndex={-1}` so activating the
 * link moves keyboard focus into the content, past the header and navigation.
 * Shared so the link target and the element can never drift apart.
 */
export const MAIN_CONTENT_ID = 'main-content';

/** Badge photo base URL — requires Midway cookie (works on *.amazon.com domains). */
export const BADGE_PHOTO_URL = 'https://badgephotos.corp.amazon.com/?uid=';

/** External link URLs shared across the app. */
export const USER_GUIDE_URL =
  'https://w.amazon.com/bin/view/INTech/TranslationServices/STRIDER/LangHub/UserGuide/';
/** LangHub help agent in Amazon Quick, opened from the header Help link. */
export const HELP_URL =
  'https://us-east-1.quicksight.aws.amazon.com/sn/account/amazonbi/start/agents?view=b229122c-7371-4cd1-8e1e-a3b7ceec6b03';
export const SLACK_CHANNEL_URL =
  'https://amazon.enterprise.slack.com/archives/C0C099A6CTU';

/**
 * Is-It-Down health dashboard for LangHub. Opened from the header health button and
 * linked from the Health Status panel; the live service status lives on this page.
 */
export const IS_IT_DOWN_URL = 'https://is-it-down.amazon.com/LangHub';

/** River workflow for LangHub tickets, linked from the footer and the header Feedback menu. */
export const CUT_TICKET_URL =
  'https://river.amazon.com/workflow/run/wfss-f71480df-2dc1-419e-9f46-2e9b43af3b7e?org=TranslationServices';

/** LangHub pilot feedback form, linked from the header Feedback menu. */
export const FEEDBACK_FORM_URL = 'https://forms.cloud.microsoft/r/cNGdWKBCvC';

/**
 * Milliseconds in one day. All LangHub API timestamps are epoch milliseconds,
 * so day-based arithmetic uses this rather than 86_400.
 */
export const MILLIS_PER_DAY = 86_400_000;

/** Milliseconds in one minute — the unit turnaround time is accumulated in. */
export const MILLIS_PER_MINUTE = 60_000;

/** Minutes in one hour, for splitting an elapsed-minutes total into "Hh MMm". */
export const MINUTES_PER_HOUR = 60;

/** Hours in one day, for rolling a long turnaround into days. */
export const HOURS_PER_DAY = 24;

/**
 * Offset from midnight to the last millisecond of the same day (23:59:59.999).
 * Used to make a date-only end bound inclusive of the whole day.
 */
export const END_OF_DAY_MILLIS = MILLIS_PER_DAY - 1;

/**
 * RUM metric name maps — the single source of truth for CloudWatch RUM custom-event and page-view
 * dimension names.
 */

/**
 * Custom RUM events LangHub emits on user click actions. Every name.
 * follows the `<action>_click_event` convention.
 */
export const RUM_CLICK_EVENT = {
  Search: 'search_click_event',
  CreateString: 'create_string_click_event',
  AddAnotherString: 'add_another_string_click_event',
  EditGroup: 'edit_group_click_event',
  VersionHistory: 'version_history_click_event',
  Rollback: 'rollback_click_event',
} as const;

export type RumClickEvent =
  (typeof RUM_CLICK_EVENT)[keyof typeof RUM_CLICK_EVENT];

/**
 * Page labels for the RUM page-view metric. Decouple the metric dimension
 * from the URL grammar (a route rename won't fragment the metric) and collapse the two landing
 * paths (`/` and `/home`) into a single `Landing` count. Consumed by resolvePageName /
 * usePageViewMetrics.
 */
export const RUM_PAGE_NAME = {
  Landing: 'Landing',
  Search: 'Search',
  Track: 'Track',
  Namespace: 'Namespace',
  StringGroup: 'StringGroup',
  StringDetail: 'StringDetail',
  /**
   * String-detail page viewed with a `?variant=<cxId>` query param. A separate label from
   * StringDetail so variant views aggregate on their own, and switching between variants (same
   * pathname, different `variant`) counts as a new page view. Kept to one label — not one per
   * cxId — so cardinality stays bounded.
   */
  Variant: 'Variant',
  /** Any route not matched above — keeps the metric total honest without exploding cardinality. */
  Other: 'Other',
} as const;

export type PageName = (typeof RUM_PAGE_NAME)[keyof typeof RUM_PAGE_NAME];
